/**
 * Internal Event Tracking System for Merchant Portal
 * Sends events to internal Metabase system via Event Gateway
 */

/* eslint-env browser */

// Types
interface EventGatewayConfig {
  endpoint: string;
  enabled: boolean;
}

interface EventGatewayConfigs {
  development: EventGatewayConfig;
  production: EventGatewayConfig;
}

interface TrackingProperties {
  category?: string;
  action?: string;
  label?: string;
  value?: number;
  additionalProperties?: Record<string, unknown>;
}

interface PageViewProperties {
  screen_name?: string;
  screen_url?: string;
  screen_type?: string;
  is_gated?: boolean;
  has_additional_data?: boolean;
  search_keyword_input?: string;
  additionalProperties?: Record<string, unknown>;
}

// Client-side payload format (matches Swagger documentation)
interface ClientEventPayload {
  event_uuid?: string;
  user_uuid?: string;
  event_type: string;
  profile_id?: string;
  domain?: string;
  timestamp: number;
  shopback_session_id?: string;
  user_agent?: string;
  web_platform?: string;
  browser_name?: string;
  browser_version?: string;
  device_type?: string;
  web_app_version?: string;
  os_version?: string;
  event_properties: Record<string, unknown>;
}

// Server-side payload format (for reference)
interface ServerEventPayload {
  event_type: string;
  data: Array<{
    timestamp: number;
    event_properties: Record<string, unknown>;
  }>;
}

interface AnalyticsConfig {
  enabled: boolean;
  endpoint: string;
  environment: string | undefined;
}

type BrowserName = 'Chrome' | 'Firefox' | 'Safari' | 'Edge' | 'Unknown';
type DeviceType = 'desktop' | 'mobile' | 'tablet';
type WebPlatform = 'desktop' | 'mobile';

// Configuration
// Using correct client-side endpoints from Swagger documentation
const EVENT_GATEWAY_CONFIG: EventGatewayConfigs = {
  development: {
    // Correct development endpoint from server list
    endpoint: 'https://tracking-dev-aps1.shopback.com/client/v1/web_events',
    enabled: true, // Enable in dev for testing
  },
  production: {
    // Correct production endpoint from server list
    endpoint: 'https://tracking-aps1.shopback.com/client/v1/web_events',
    enabled: true,
  },
};

class InternalAnalytics {
  private config: EventGatewayConfig;
  private enabled: boolean;
  private currentUser: { id?: string; email?: string } | null = null;

  constructor() {
    this.config =
      EVENT_GATEWAY_CONFIG[process.env.NODE_ENV as keyof EventGatewayConfigs] || EVENT_GATEWAY_CONFIG.development;
    this.enabled = this.config.enabled && !!this.config.endpoint;

    if (this.enabled) {
      console.log(`[Internal Analytics] Initialized for ${process.env.NODE_ENV} environment`);
      console.log(`[Internal Analytics] Endpoint: ${this.config.endpoint}`);
    } else {
      console.warn('[Internal Analytics] Disabled - no endpoint configured');
    }
  }

  /**
   * Set current user information for analytics tracking
   * This should be called when user data is available from AppContext
   */
  setUser(user: { id?: string; email?: string } | null): void {
    this.currentUser = user;
    if (process.env.NODE_ENV !== 'production') {
      console.log('[Internal Analytics] User set:', { userId: user?.id, email: user?.email });
    }
  }

  /**
   * Track MerchantPortal.Web.View.Screen events (for page views)
   * Automatically adds merchant portal identification for data separation
   */
  async trackWebViewScreen(properties: PageViewProperties = {}): Promise<void> {
    if (!this.enabled) {
      console.log('[Internal Analytics] Tracking disabled, skipping Web.View.Screen event');
      return;
    }

    // Use client-side payload format (matches Swagger example)
    const payload: ClientEventPayload = {
      event_type: 'MerchantPortal.Web.View.Screen',
      timestamp: Date.now(),
      user_uuid: this.getUserUuid(),
      profile_id: this.generateProfileID(),
      domain: typeof window !== 'undefined' ? window.location.hostname : undefined,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      web_platform: this.getWebPlatform(),
      browser_name: this.getBrowserName(),
      browser_version: this.getBrowserVersion(),
      device_type: this.getDeviceType(),
      event_properties: {
        // Essential web context (no templates)
        is_logged_in: this.isUserLoggedIn(),
        screen_url: properties.screen_url || (typeof window !== 'undefined' ? window.location.href : ''),
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',

        // MerchantPortal.Web.View.Screen specific fields
        portal_section: this.getPortalSection(),
        is_gated: properties.is_gated || false,
        has_additional_data: properties.has_additional_data || false,
        search_keyword_input: properties.search_keyword_input || undefined,

        // Include any additional properties
        ...properties.additionalProperties,
      },
    };

    try {
      await this.sendClientEvent(payload);

      // Development logging
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Internal Analytics] Web.View.Screen event sent:', payload);
      }
    } catch (error) {
      console.error('[Internal Analytics] Failed to send Web.View.Screen event:', error);
    }
  }

  /**
   * Track a MerchantPortal.Web.Click event
   * Automatically adds merchant portal identification for data separation
   */
  async trackWebClick(properties: TrackingProperties = {}): Promise<void> {
    if (!this.enabled) {
      console.log('[Internal Analytics] Tracking disabled, skipping event');
      return;
    }

    // Use client-side payload format (matches Swagger example)
    const payload: ClientEventPayload = {
      event_type: 'MerchantPortal.Web.Click',
      timestamp: Date.now(),
      user_uuid: this.getUserUuid(),
      profile_id: this.generateProfileID(),
      domain: typeof window !== 'undefined' ? window.location.hostname : undefined,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      web_platform: this.getWebPlatform(),
      browser_name: this.getBrowserName(),
      browser_version: this.getBrowserVersion(),
      device_type: this.getDeviceType(),
      event_properties: {
        // Essential web context (no templates)
        is_logged_in: this.isUserLoggedIn(),
        screen_url: typeof window !== 'undefined' ? window.location.href : '',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',

        // Map interact_type to allowed enum values
        interact_type: this.mapInteractType(properties.action || 'click'),

        // MerchantPortal.Web.Click specific fields
        component_uid: properties.label || undefined,
        portal_section: this.getPortalSection(),
        action_category: this.mapActionCategory(properties.category),
        button_text: properties.label || undefined,

        // Original event data for backward compatibility
        event_value: properties.value || undefined,
        original_category: properties.category || undefined,
        original_action: properties.action || undefined,
        original_label: properties.label || undefined,

        // Include any additional properties that match the schema
        ...properties.additionalProperties,
      },
    };

    try {
      await this.sendClientEvent(payload);

      // Development logging
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Internal Analytics] Web.Click event sent:', payload);
      }
    } catch (error) {
      console.error('[Internal Analytics] Failed to send Web.Click event:', error);
    }
  }

  /**
   * Send client event using correct client-side payload format
   */
  private async sendClientEvent(payload: ClientEventPayload): Promise<unknown> {
    const response = await fetch(this.config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Utility methods for gathering web properties
   */
  private getScreenName(): string {
    if (typeof window === 'undefined' || !window.location) return 'Unknown Screen';

    const pathname = window.location.pathname;
    const segments = pathname.split('/').filter(Boolean);

    if (segments.length === 0) return 'Home';

    // Convert path to readable screen name
    return segments
      .map((segment) =>
        segment
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      )
      .join(' - ');
  }

  private getDefaultScreenType(): string {
    if (typeof window === 'undefined' || !window.location) return 'settings_form';

    const pathname = window.location.pathname;

    // Map paths to appropriate screen types based on web_screen template enum values
    if (pathname === '/' || pathname === '/home') return 'home';
    if (pathname.includes('/login')) return 'login';
    if (pathname.includes('/signup')) return 'signup';
    if (pathname.includes('/account') || pathname.includes('/profile')) return 'account';
    if (pathname.includes('/search')) return 'search';
    if (pathname.includes('/settings')) return 'settings_form';
    if (pathname.includes('/payment')) return 'payment';
    if (pathname.includes('/security')) return 'security';
    if (pathname.includes('/withdrawal')) return 'withdrawal';
    if (pathname.includes('/transaction')) return 'transaction_history';
    if (pathname.includes('/support')) return 'support';

    // Default for merchant portal admin interface
    return 'settings_form';
  }

  private mapInteractType(action: string): string {
    // Map action types to MerchantPortal.Web.Click allowed enum values
    const actionMap: Record<string, string> = {
      click: 'click',
      save: 'save',
      create: 'create',
      edit: 'edit',
      delete: 'delete',
      export: 'export',
      download: 'download',
      submit: 'submit',
      cancel: 'cancel',
      navigate: 'navigate',
      search: 'search',
      filter: 'filter',
      login: 'login',
      logout: 'logout',
      // Legacy event mappings
      'Test Button Click': 'click',
      'Next CTAs': 'navigate',
      'Cancel CTAs': 'cancel',
      'Submit CTAs': 'submit',
      'Export CTA': 'export',
      'Download Case Studies': 'download',
      'Playbook CTA': 'click',
      Broadcast: 'create',
      'Create notification': 'create',
      'Step 2 - Input Message': 'click',
    };

    return actionMap[action] || 'click'; // Default to 'click' if not mapped
  }

  private getBrowserName(): BrowserName {
    if (typeof navigator === 'undefined') return 'Unknown';

    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  private getBrowserVersion(): string {
    return typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown';
  }

  private getWebPlatform(): WebPlatform {
    return typeof window !== 'undefined' && window.innerWidth <= 768 ? 'mobile' : 'desktop';
  }

  private getDeviceType(): DeviceType {
    if (typeof navigator === 'undefined') return 'desktop';

    const userAgent = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(userAgent)) return 'tablet';
    if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent))
      return 'mobile';
    return 'desktop';
  }

  private isUserLoggedIn(): boolean {
    // Check if user data is set via setUser method
    return !!this.currentUser?.id;
  }

  private getUserUuid(): string | undefined {
    // Use explicitly set user data (set via setUser method in App.js)
    return this.currentUser?.id;
  }

  /**
   * Generate or retrieve profile ID using data team's standard method
   * Format: web_{32_random_chars}_{timestamp}
   * Persists in localStorage for consistent device tracking
   */
  private generateProfileID(): string {
    // Return server placeholder if not in browser environment
    if (typeof window === 'undefined') return 'server_unknown';

    const storageKey = 'shopback_web_profile_id';
    let profileId = localStorage.getItem(storageKey);

    if (!profileId) {
      // Generate a new profile ID using data team's standard method
      let text = 'web_';
      const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

      for (let i = 0; i < 32; i++) {
        text += possible.charAt(Math.floor(Math.random() * possible.length));
      }

      text += '_' + new Date().getTime();
      profileId = text;

      try {
        localStorage.setItem(storageKey, profileId);
      } catch (e) {
        // Handle localStorage errors (e.g., private browsing)
        console.warn('[Analytics] Could not store profile_id in localStorage');
      }
    }

    return profileId;
  }

  private isFirstEntry(): boolean {
    // Simple implementation - could be more sophisticated
    if (typeof window === 'undefined') return false;
    return !sessionStorage.getItem('hasVisited');
  }

  private getPortalSection(): string {
    if (typeof window === 'undefined' || !window.location) return 'navigation';

    const path = window.location.pathname;

    if (path.includes('/overview')) return 'overview';
    if (path.includes('/campaigns')) return 'campaigns';
    if (path.includes('/crm')) return 'crm';
    if (path.includes('/customer-insights')) return 'customer_insights';
    if (path.includes('/performance-insights')) return 'performance_insights';
    if (path.includes('/opportunity')) return 'opportunity';
    if (path.includes('/outlet-codes')) return 'outlet_codes';
    if (path.includes('/downloads')) return 'downloads';
    if (path.includes('/finance')) return 'finance';
    if (path.includes('/settings')) return 'settings';
    if (path.includes('/admin')) return 'admin';
    if (path.includes('/login')) return 'login';
    if (path.includes('/register')) return 'register';
    if (path.includes('/reset-password')) return 'reset_password';
    if (path.includes('/404')) return 'error_404';

    return 'navigation';
  }

  private mapActionCategory(category?: string): string {
    if (!category) return 'navigation';

    const categoryLower = category.toLowerCase();

    if (categoryLower.includes('nav') || categoryLower.includes('menu')) return 'navigation';
    if (categoryLower.includes('export') || categoryLower.includes('download')) return 'data_export';
    if (categoryLower.includes('campaign')) return 'campaign_management';
    if (categoryLower.includes('customer') || categoryLower.includes('crm')) return 'customer_management';
    if (categoryLower.includes('notification') || categoryLower.includes('broadcast')) return 'customer_management';
    if (categoryLower.includes('report') || categoryLower.includes('insight')) return 'report_viewing';
    if (categoryLower.includes('setting')) return 'settings_update';
    if (categoryLower.includes('admin')) return 'admin_action';
    if (categoryLower.includes('user')) return 'user_management';

    return 'navigation';
  }

  /**
   * Validate event payload against dev endpoint
   */
  async validateEvent(payload: ClientEventPayload | ServerEventPayload): Promise<boolean> {
    try {
      const response = await fetch('https://tracking-validator.dev.shopback-data.com/validate/egw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        console.log('[Internal Analytics] Event validation passed:', result);
        return true;
      } else {
        console.error('[Internal Analytics] Event validation failed:', result);
        return false;
      }
    } catch (error) {
      console.error('[Internal Analytics] Event validation error:', error);
      return false;
    }
  }

  /**
   * Get current configuration for debugging
   */
  getConfig(): AnalyticsConfig {
    return {
      enabled: this.enabled,
      endpoint: this.config.endpoint,
      environment: process.env.NODE_ENV,
    };
  }
}

// Create singleton instance
const internalAnalytics = new InternalAnalytics();

// Export convenience functions
export const trackWebClick = (properties?: TrackingProperties): Promise<void> =>
  internalAnalytics.trackWebClick(properties);
export const trackWebViewScreen = (properties?: PageViewProperties): Promise<void> =>
  internalAnalytics.trackWebViewScreen(properties);
export const validateEvent = (payload: ClientEventPayload | ServerEventPayload): Promise<boolean> =>
  internalAnalytics.validateEvent(payload);
export const getInternalAnalyticsConfig = (): AnalyticsConfig => internalAnalytics.getConfig();
export const setAnalyticsUser = (user: { id?: string; email?: string } | null): void => internalAnalytics.setUser(user);

export default internalAnalytics;

// Export types for use in other files
export type { TrackingProperties, PageViewProperties, ClientEventPayload, ServerEventPayload, AnalyticsConfig };
